import { Component } from "@theme/component";
import {
  onDocumentLoaded,
  changeMetaThemeColor,
  setHeaderMenuStyle,
} from "@theme/utilities";

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ["headerDrawerContainer", "headerMenu", "headerRowTop"];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Ignore tiny scroll fluctuations to prevent sticky state flicker on mobile.
   * @type {number}
   */
  #scrollDeltaThreshold = 4;

  /**
   * The first main-content section — the "hero" a `transparent="past-hero"`
   * header overlays. Looked up lazily and cached; `undefined` means not yet
   * looked up, `null` means there is none.
   * @type {Element | null | undefined}
   */
  #heroSection;

  /**
   * The media query the logo's shrink on scroll runs under, from
   * `data-logo-shrink`. `null` when the logo block has it switched off, and
   * on every page but the homepage.
   * @type {MediaQueryList | null}
   */
  #logoShrinkQuery = null;

  /**
   * How far the page scrolls while the logo shrinks; 0 while it is not shrinking
   * @type {number}
   */
  #logoShrinkDistance = 0;

  /**
   * The scroll position the shrink starts at: where the header pins
   * @type {number}
   */
  #logoShrinkStart = 0;

  /**
   * Whether CSS runs the shrink as a scroll-driven animation, on the
   * compositor. Script then only measures; without it, script drives
   * `--logo-progress` from the scroll position.
   */
  #logoShrinkInCss = CSS.supports("animation-timeline", "scroll()");

  /**
   * The last `--logo-progress` written, to skip writes that change nothing
   * @type {number | null}
   */
  #logoProgress = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty(
      "--header-height",
      `${roundedHeaderHeight}px`,
    );

    // Check if the menu drawer should be hidden in favor of the header menu
    if (
      this.#menuDrawerHiddenWidth &&
      window.innerWidth > this.#menuDrawerHiddenWidth
    ) {
      this.#updateMenuVisibility(false);
    }

    // The small logo's place moves with the header's layout
    if (this.#logoShrinkQuery) this.#measureLogoShrink();
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? "inactive" : "active";
        if (this.dataset.themeColor)
          changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen =
          !isIntersecting || this.dataset.stickyState === "active";
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
      this.#updateAtPageBottom();
      this.#updatePastHero();
      this.#updateLogoShrink();
    });
  };

  /**
   * Measures the logo's shrink on scroll: the transform that takes the large
   * logo beneath the header onto the small logo's place in it, and the scroll
   * distance that takes. `offset*` and the sizer's rect ignore the transform
   * already applied, so this can run at any scroll position. Clears everything
   * when the media query no longer matches, which hands the logo back to its
   * static styles.
   */
  #measureLogoShrink = () => {
    this.#logoShrinkDistance = 0;

    if (this.#logoShrinkQuery?.matches) {
      const sizer = this.querySelector(".header-logo__sizer");
      const large = Array.from(
        this.querySelectorAll(".header-logo__image-container"),
      ).find((container) => container instanceof HTMLElement && container.offsetWidth > 0);
      const row = large instanceof HTMLElement ? large.offsetParent : null;

      if (sizer && large instanceof HTMLElement && row instanceof HTMLElement) {
        const sizerRect = sizer.getBoundingClientRect();
        const rowRect = row.getBoundingClientRect();
        const scale = Math.min(
          sizerRect.width / large.offsetWidth,
          sizerRect.height / large.offsetHeight,
        );
        const gap = large.offsetTop - row.clientHeight;
        // The large logo is centred on its own `left`, and scales from its top centre
        const x = sizerRect.left + sizerRect.width / 2 - (rowRect.left + row.clientLeft + large.offsetLeft);
        const y =
          sizerRect.top + (sizerRect.height - large.offsetHeight * scale) / 2 -
          (rowRect.top + row.clientTop + large.offsetTop);

        // The header pins once the sections above it, less its sticky `top`, have scrolled away
        const section = this.parentElement;
        let start = 0;
        let sibling = section?.previousElementSibling;
        while (sibling) {
          start += sibling.getBoundingClientRect().height;
          sibling = sibling.previousElementSibling;
        }
        if (section && getComputedStyle(section).position === "sticky") {
          start -= parseFloat(getComputedStyle(section).top) || 0;
        }

        this.style.setProperty("--logo-shrink-scale", scale.toFixed(4));
        this.style.setProperty("--logo-shrink-x", `${x.toFixed(2)}px`);
        this.style.setProperty("--logo-shrink-y", `${y.toFixed(2)}px`);
        this.#logoShrinkStart = Math.max(start, 0);
        this.#logoShrinkDistance = large.offsetHeight + gap * 2;
        this.style.setProperty("--logo-shrink-start", `${this.#logoShrinkStart.toFixed(2)}px`);
        this.style.setProperty("--logo-shrink-distance", `${this.#logoShrinkDistance}px`);
      }
    }

    if (this.#logoShrinkDistance <= 0) {
      this.#logoProgress = null;
      for (const property of ["scale", "x", "y", "start", "distance"]) {
        this.style.removeProperty(`--logo-shrink-${property}`);
      }
      this.style.removeProperty("--logo-progress");
      return;
    }

    this.#updateLogoShrink();
  };

  /**
   * Drives `--logo-progress` from 0 (large, beneath the header) to 1 (small,
   * in the header) over the distance the large logo takes up, starting once
   * the header is pinned. Only for browsers without scroll-driven animations;
   * it is the same mapping as the CSS one. A pull past the top of the page
   * (iOS reports a negative scroll) stays at 0, so the logo holds its size.
   */
  #updateLogoShrink = () => {
    if (this.#logoShrinkInCss || this.#logoShrinkDistance <= 0) return;

    const travelled = window.scrollY - this.#logoShrinkStart;
    const progress = Math.min(Math.max(travelled / this.#logoShrinkDistance, 0), 1);

    if (progress === this.#logoProgress) return;
    this.#logoProgress = progress;
    this.style.setProperty("--logo-progress", progress.toFixed(4));
  };

  /**
   * Toggles `data-at-page-bottom` when the viewport is within one header
   * height of the bottom of the page, so the footer can restyle the header
   * to contrast with its own background.
   */
  #updateAtPageBottom = () => {
    const scroller = document.scrollingElement;
    if (!scroller) return;

    const remaining =
      scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    this.toggleAttribute("data-at-page-bottom", remaining <= this.offsetHeight);
  };

  /**
   * Toggles `data-past-hero` once the hero section a `transparent="past-hero"`
   * header overlays has scrolled behind it, so the header can switch from
   * transparent to its default styling — mirroring how `data-at-page-bottom`
   * lets the footer restyle the header at the bottom of the page.
   */
  #updatePastHero = () => {
    if (this.getAttribute("transparent") !== "past-hero") return;

    if (this.#heroSection === undefined) {
      this.#heroSection = document.querySelector("main > .shopify-section:first-child");
    }
    if (!this.#heroSection) return;

    const heroBottom = this.#heroSection.getBoundingClientRect().bottom;
    this.toggleAttribute("data-past-hero", heroBottom <= this.offsetHeight);
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute("sticky");
    if (!this.#offscreen && stickyMode !== "always") return;

    const scrollTop = Math.max(document.scrollingElement?.scrollTop ?? 0, 0);
    const scrollDelta = scrollTop - this.#lastScrollTop;
    const headerTop = this.getBoundingClientRect().top;
    const isAtDocumentTop = scrollTop <= this.#scrollDeltaThreshold;
    const isAtTop = headerTop >= 0 || isAtDocumentTop;

    if (stickyMode !== "always" && isAtDocumentTop) {
      this.#offscreen = false;
      this.dataset.stickyState = "inactive";
      this.dataset.scrollDirection = "none";
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (!isAtTop && Math.abs(scrollDelta) < this.#scrollDeltaThreshold) return;

    const isScrollingUp = scrollDelta < 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === "always") {
      if (isAtTop) {
        this.dataset.scrollDirection = "none";
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = "up";
      } else {
        this.dataset.scrollDirection = "down";
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = "inactive";
        this.dataset.scrollDirection = "none";
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = "active";
        this.dataset.scrollDirection = "up";
      }
    } else if (this.dataset.stickyState === "active") {
      this.dataset.scrollDirection = "none";

      this.dataset.stickyState = "idle";
    } else {
      this.dataset.scrollDirection = "none";
      this.dataset.stickyState = "idle";
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener("overflowMinimum", this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute("sticky");
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === "always");

      if (stickyMode === "scroll-up" || stickyMode === "always") {
        document.addEventListener("scroll", this.#handleWindowScroll);
        this.#updateAtPageBottom();
        this.#updatePastHero();
      }
    }

    const logoShrinkMedia = this.dataset.logoShrink;
    if (logoShrinkMedia) {
      this.#logoShrinkQuery = matchMedia(logoShrinkMedia);
      this.#logoShrinkQuery.addEventListener("change", this.#measureLogoShrink);
      window.addEventListener("resize", this.#measureLogoShrink);
      document.addEventListener("scroll", this.#handleWindowScroll);
      this.#measureLogoShrink();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#logoShrinkQuery?.removeEventListener("change", this.#measureLogoShrink);
    this.#logoShrinkQuery = null;
    window.removeEventListener("resize", this.#measureLogoShrink);
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener("overflowMinimum", this.#handleOverflowMinimum);
    document.removeEventListener("scroll", this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty("--header-height", "0px");
  }
}

if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector("header-component");
  const headerGroup = document.querySelector("#header-group");

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute("transparent") &&
            header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty(
        "--header-group-height",
        `${roundedHeaderGroupHeight}px`,
      );
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
